import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, ArrowLeftRight, Loader2 } from "lucide-react";

/**
 * DemoWorkspaceBanner — high-contrast amber banner that appears
 * whenever the caller is pivoted into the read-only `global-demo`
 * workspace via POST /api/workspace/switch. Self-hides when they
 * return to their real workspace. Renders nothing for users who
 * never entered demo mode.
 *
 * Uses `/api/workspace/current` (introspection) so the banner
 * shows in ANY tab where the demo cookie is set — not just the
 * one where they pressed the button.
 */
export default function DemoWorkspaceBanner() {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetchState = () =>
            api.get("/workspace/current")
                .then((r) => { if (!cancelled) setState(r.data); })
                .catch(() => { if (!cancelled) setState(null); });
        fetchState();
        const iv = setInterval(fetchState, 45_000);
        return () => { cancelled = true; clearInterval(iv); };
    }, []);

    if (!state?.is_demo) return null;

    const exitDemo = async () => {
        setBusy(true);
        try {
            await api.post("/workspace/return");
            // Force a hard reload so every open module refetches from the
            // caller's home tenant rather than the demo one.
            window.location.reload();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="demo-workspace-banner"
            className="w-full bg-gradient-to-r from-amber-500 via-amber-400 to-orange-400 text-stone-900 px-4 py-2.5 flex items-center gap-3 shadow-sm border-b border-amber-600"
        >
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/90 text-[10px] font-bold uppercase tracking-widest text-amber-800 shrink-0">
                <Sparkles size={11} /> Demo Mode
            </span>
            <div className="text-[13px] flex-1 min-w-0">
                <span className="font-semibold">You&rsquo;re exploring the VAULT Demo workspace.</span>{" "}
                <span className="hidden sm:inline text-stone-800/90">All numbers are fictional. Changes are disabled.</span>
            </div>
            <button
                onClick={exitDemo}
                disabled={busy}
                data-testid="demo-workspace-exit-btn"
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-900 text-white text-[12px] font-semibold hover:bg-stone-800 transition-colors disabled:opacity-60"
            >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <ArrowLeftRight size={13} />}
                Return to my workspace
            </button>
        </div>
    );
}
